/*
# -----------------------------------------------------------------------------
# Component: debitNoteAjaxMixin.js
# Author: antonio.ramirez@pst.sg
# Created Date: 12/May/2019
# Comments: Responsible for all backend related calls for Manage DebitNote
# -----------------------------------------------------------------------------
*/
import MessageMixin from '@/mixins/message'
import { expandURI, filterQuery } from '@/utils/ajax'
import { formatDisplayDate, formatDisplayDateTime2, formatDisplayDateTime4, removeTime } from '@/utils/date'
import clone from 'clone'
import { formatPrice } from '@/utils/index'
import { DEBITNOTE_LOAD_DATA, CHG_ACCT_MCH_RECKEY } from '@/store/types'
import { recordLockMixin } from '@/mixins/common/recordLockMixin'
import { accountsAjaxCalls } from '@/mixins/accounting/accountsAjaxCalls'
import { strJoin, checkNull } from '@/utils/stringutil'

export const debitNoteAjaxMixin = {
  data() {
    return {
      actionName: ''
    }
  },
  created() {
    // AR: Initialize CommonUtil Menu 29-Jan-2021
    this.initCommonUtilMenu()
  },
  methods: {
    performSearchFilters() {
      let massageFilters = clone(this.debitNoteForm)
      if (massageFilters.transactionDateFrom !== null && massageFilters.transactionDateFrom !== '') {
        massageFilters.transactionDateFrom = formatDisplayDateTime2(removeTime(massageFilters.transactionDateFrom))
      }
      if (massageFilters.transactionDateTo !== null && massageFilters.transactionDateTo !== '') {
        massageFilters.transactionDateTo = formatDisplayDateTime2(removeTime(massageFilters.transactionDateTo))
      }
      return massageFilters
    },
    async performSearch() {
      const apiUrl = this.$store.state.debitNote.menu.performSearch
      if (apiUrl) {
        try {
          this.loading = true
          const { page, itemsPerPage } = this.pagination
          const paramMap = {
            page: page - 1,
            size: itemsPerPage
          }

          const result = await this.$http.get(filterQuery(expandURI(apiUrl, paramMap), this.performSearchFilters(), this.pagination.sortBy, this.pagination.sortDesc))
          const { data } = result.data
          if (data) {
            this.totalItems = result.data.totalElements
            this.items = []
            data.map((result) => {
              const resultList = {
                accountNo: result.acctNo,
                branch: result.branch,
                currency: result.curr,
                documentNo: result.docNo,
                tranDate: this.formatDate(result.tranDate),
                amount: formatPrice(result.debitAmt),
                gst: formatPrice(result.othGST),
                suffix: result.suffix,
                fund: result.fund,
                type: result.type
              }
              this.items.push(resultList)
            })
          } else {
            this.items = []
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        } finally {
          this.loading = false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url.')
        this.loading = false
      }
    },
    performSaveUnlockRecord(recKey, docNo) {
      if (!(this.debitNoteForm.actionName === 'CREATE_SAVE_POST' ||
        this.debitNoteForm.actionName === 'CREATE_SAVE_REVIEW' ||
        this.debitNoteForm.actionName === 'CREATE_MATCH_NOW' ||
        this.debitNoteForm.actionName === 'UPDATE_MATCH_NOW') &&
        (docNo !== '' && docNo !== undefined && docNo !== null)) {
        // AR: OS-660 Unlock Record upon exit 29-Jan-2021
        this.unlockRecord(recKey, 'ACT')
        this.$store.commit(CHG_ACCT_MCH_RECKEY, '')
      }
    },
    performSaveFilters() {
      let massageFilters = clone(this.debitNoteForm)
      if (massageFilters.transactionDate !== '') {
        massageFilters.transactionDate = formatDisplayDate(massageFilters.transactionDate)
      }
      return massageFilters
    },
    performSaveNavigate() {
      if (this.debitNoteForm.actionName === 'CREATE_SAVE_REVIEW' || this.debitNoteForm.actionName === 'UPDATE_SAVE_REVIEW') {
        this.$router.push({ name: 'debitNote' })
      } else {
        this.$router.push({ name: 'glDebitNote' })
      }
    },
    async performSave() {
      const apiURI = this.$store.state.debitNote.menu.performAction
      if (apiURI) {
        try {
          const result = await this.$http.post(apiURI, this.performSaveFilters())
          const { data } = result
          if (data) {
            const debitNoteInfo = {
              tranMode: 'R',
              branch: this.debitNoteForm.branch,
              accountNo: this.debitNoteForm.accountNo,
              type: this.debitNoteForm.transactionType,
              documentNo: data.resultMap.documentNo,
              fund: this.debitNoteForm.fund,
              suffix: this.debitNoteForm.accountNoSuffix,
              currency: this.debitNoteForm.netAmountCurr,
              amount: this.debitNoteForm.amount,
              gstAE: this.debitNoteForm.gstAmountAE,
              gst: this.debitNoteForm.gstAmount,
              netAmt: this.debitNoteForm.netAmount,
              netLocAmtAE: this.debitNoteForm.netAmountLocalAE,
              netLocAmt: this.debitNoteForm.netLocalAmount,
              tranDate: this.debitNoteForm.transactionDate,
              actionName: this.debitNoteForm.actionName,
              printDoc: this.debitNoteForm.printDocument
            }
            this.$store.commit(DEBITNOTE_LOAD_DATA, debitNoteInfo)

            let docNo = this.getDocumentNo(this.debitNoteForm.documentNo, data.resultMap.documentNo, this.debitNoteForm.actionName)
            this.debitNoteForm.documentNo = data.resultMap.documentNo

            let recKey = strJoin('|', this.debitNoteForm.transactionType, this.debitNoteForm.branch,
              this.debitNoteForm.accountNo, docNo, this.debitNoteForm.fund, this.debitNoteForm.accountNoSuffix)

            if (this.debitNoteForm.actionName === 'CREATE_MATCH_NOW') {
              this.$store.commit(CHG_ACCT_MCH_RECKEY, recKey)
              this.checkRecordLock(recKey, 'ACT')
            }

            this.notify(data)
            this.performSaveUnlockRecord(recKey, docNo)
            this.performSaveNavigate()
            this.doExit()
          }
        } catch (err) {
          const { data } = err.response
          this.showPromptLeave = true
          if (data) {
            this.processingFlag = false
            this.message(data)
            this.fieldMessage(data)
          }
        }
      } else {
        this.showPromptLeave = true
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async performReview() {
      const apiURI = this.$store.state.debitNote.menu.performAction
      if (apiURI) {
        try {
          this.debitNoteForm.actionName = 'DEBITNOTE_REVIEW'
          this.debitNoteForm.branch = this.debitNoteData.branch
          this.debitNoteForm.accountNo = this.debitNoteData.accountNo
          this.debitNoteForm.transactionType = this.debitNoteData.type
          this.debitNoteForm.documentNo = this.debitNoteData.documentNo
          this.debitNoteForm.fund = this.debitNoteData.fund
          this.debitNoteForm.accountNoSuffix = this.debitNoteData.suffix
          const response = await this.$http.post(apiURI, this.debitNoteForm)
          const { data } = response.data
          if (data) {
            this.$set(this, 'debitNoteForm', data.accounts)
            this.debitNoteForm.amount = formatPrice(data.accounts.amount)
            this.debitNoteForm.gstAmount = formatPrice(data.accounts.gstAmount)
            this.debitNoteForm.netAmount = formatPrice(data.accounts.netAmount)
            this.debitNoteForm.netLocalAmount = formatPrice(data.accounts.netLocalAmount)
            this.debitNoteForm.docAE = data.accounts.docAE === 'true'
            this.debitNoteForm.gstAmountAE = data.accounts.gstAmountAE === 'true'
            this.debitNoteForm.netAmountLocalAE = data.accounts.netAmountLocalAE === 'true'
            this.debitNoteForm.oldFund = checkNull(data.accounts.fund)
            this.debitNoteForm.oldAccountNoSuffix = checkNull(data.accounts.accountNoSuffix)
            this.debitNoteForm.oldAccountNo = checkNull(data.accounts.accountNo)
            this.debitNoteForm.policyNo = ''
            if (data.accounts.polNo !== '' && data.accounts.polNo !== null) {
              this.debitNoteForm.policyNo = data.accounts.polNo + '-' + data.accounts.renCnt + '-' + data.accounts.endtCnt
            }
            this.fetchUserDetails(data.accounts.createdBy).then(result => {
              this.debitNoteForm.createdBy = result
            })
            this.debitNoteForm.createdDate = formatDisplayDateTime4(data.accounts.createdDate)
            this.fetchUserDetails(data.accounts.modifiedBy).then(result => {
              this.debitNoteForm.modifiedBy = result
            })
            this.debitNoteForm.modifiedDate = formatDisplayDateTime4(data.accounts.modifiedDate)
          }
        } catch (err) {
          const { data } = err.response
          this.showPromptLeave = true
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
        }
      } else {
        this.showPromptLeave = true
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async fetchBranch() {
      this.branches = []
      let apiUrl = this.$store.state.debitNote.menu.fetchBranch
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.branches.push(data)
              })
            }
          }
        })
      }
    },
    async fetchDepartment() {
      this.departments = []
      let apiUrl = this.$store.state.debitNote.menu.fetchDepartment
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.departments.push(data)
              })
            }
          }
        })
      }
    },
    async fetchFund() {
      this.funds = []
      let apiUrl = this.$store.state.debitNote.menu.fetchFund
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.funds.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAmountCurrency() {
      this.amountcurrencies = []
      let apiUrl = this.$store.state.debitNote.menu.fetchAmountCurrency
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.amountcurrencies.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAccount() {
      this.accounts = []
      let apiUrl = this.$store.state.debitNote.menu.fetchAccount
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.accounts.push(data)
              })
            }
          }
        })
      }
    },
    async fetchAcctNoSfx60() {
      this.acctnosuffixes60 = []
      let apiUrl = this.$store.state.debitNote.menu.fetchAcctNoSfx60
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.acctnosuffixes60.push(data)
              })
            }
          }
        })
      }
    },
    async fetchGstStatus() {
      this.gstStatuses = []
      let apiUrl = this.$store.state.debitNote.menu.fetchGstStatus
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.gstStatuses.push(data)
              })
            }
          }
        })
      }
    },
    async fetchCurrency() {
      this.currencies = []
      let apiUrl = this.$store.state.debitNote.menu.fetchCurrency
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.currencies.push(data)
              })
            }
          }
        })
      }
    },
    async fetchBranchWBranchUserId() {
      this.branches = []
      let apiUrl = this.$store.state.debitNote.menu.fetchBranchWBranchUserId
      if (apiUrl) {
        await this.$http.get(apiUrl).then(result => {
          if (result.data) {
            const { dataList } = result.data
            if (dataList) {
              dataList.map(data => {
                this.branches.push(data)
              })
            }
          }
        })
      }
    },
    async fetchUserDetails(userid) {
      let username = '-'
      let apiUrl = this.$store.state.debitNote.menu.fetchUserDetails
      if (apiUrl) {
        const expand = {
          userId: userid
        }
        await this.$http.get(expandURI(apiUrl, expand)).then(result => {
          if (result.data) {
            const data = result.data.dataMap
            const { userDetails } = data
            username = userDetails.firstName
            if (userDetails.lastName !== null || userDetails.lastName !== '') {
              username = username + ' ' + userDetails.lastName
            }
          }
        })
      }
      return username
    },
    checkFiltersNullValue() {
      const ignore = ['transactionType', 'fund', 'transactionDate', 'actionName', 'applyFilters']
      return Object.keys(this.debitNoteForm)
        .filter(key => !ignore.includes(key))
        .every(key => this.debitNoteForm[key] === null ||
          this.debitNoteForm[key] === '' ||
          this.debitNoteForm[key] === undefined
        )
    },
    doExportFilters() {
      if (this.checkFiltersNullValue()) {
        this.exportList.promptFilterWarning = true
      } else {
        this.exportList.promptFilterWarning = false
        this.doExportList()
      }
    },
    async doExportList() {
      let columns = []
      let columnsTitle = []
      this.debitNoteColumns.map(t => {
        columnsTitle.push(t.text)
        columns.push(t.value)
      })
      const apiUrl = this.$store.state.debitNote.menu.exportAcctTypeFilteredList
      if (apiUrl) {
        try {
          let massageForm = clone(this.debitNoteForm)
          massageForm.acctType = 'Debit Note'
          massageForm.columns = columns.join(',')
          massageForm.columnsTitle = columnsTitle.join(',')
          await this.$http.post(apiUrl, massageForm)
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    }
  },
  mixins: [
    MessageMixin,
    recordLockMixin,
    accountsAjaxCalls
  ]
}
